import { useMutation } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { azureObjectUpload } from '../api/azureObjectUpload';

import { getAzureSignedObjectLink } from '../api/getAzureSignedObjectLink';
import { getFileNameWithFixedJpe } from '../../../common/utils';
import { queueFileUpload } from '../../../common/offline/fileStorage';
import { addLocalPhoto } from '../../../common/offline/offlinePhotoStorage';
import { isCurrentlyOnline, useOnlineStatus } from '../../../common/offline/useOnlineStatus';

/**
 * Parameters for uploading an object photo
 * Entity context is used by fileSyncManager to send the correct mutation after upload
 */
type UploadParams = {
  file: File;
  /** Entity type (e.g., 'trunk', 'switch', 'odf') - used for offline sync mutations */
  entityType?: string;
  /** Entity ID (node ID) - used for offline sync mutations */
  entityId?: string;
  /** Property ID - used for offline sync mutations */
  propertyId?: number;
};

type UploadResult = {
  url: string;
  isOfflineQueued: boolean;
  uploadId?: string;
};

/**
 * Queue file for offline upload and return local URL
 */
async function queueFileForOfflineUpload(
  file: File,
  entityType: string,
  entityId: string,
  propertyId: number,
): Promise<UploadResult> {
  const pendingUpload = await queueFileUpload(file, entityType, entityId, propertyId, 'object');

  // Track the local URL in offlinePhotoStorage for display and cleanup
  if (pendingUpload.localUrl) {
    addLocalPhoto(entityType, entityId, pendingUpload.localUrl, pendingUpload.id);
  }

  // Return the local URL for immediate display
  return {
    url: pendingUpload.localUrl!,
    isOfflineQueued: true,
    uploadId: pendingUpload.id,
  };
}

export const useAzureObjectPhotoUpload = () => {
  const { isOnline } = useOnlineStatus();

  const { data, mutate, isLoading, isError, isSuccess } = useMutation<UploadResult, unknown, UploadParams>(
    async ({ file, entityType = 'unknown', entityId = 'unknown', propertyId = 0 }) => {
      // Check online status at mutation time
      if (!isCurrentlyOnline()) {
        return queueFileForOfflineUpload(file, entityType, entityId, propertyId);
      }

      // Online - try to upload directly to Azure
      try {
        const { url, query } = await getAzureSignedObjectLink();

        const fileName = `${uuidv4()}-${getFileNameWithFixedJpe(file.name!)}`;
        await azureObjectUpload(new File([file!], fileName!), `${url}?${query}`);

        return {
          url: `${url}objects/${fileName}`,
          isOfflineQueued: false,
        };
      } catch (error) {
        // If the online upload fails (network error), fall back to offline queue
        return queueFileForOfflineUpload(file, entityType, entityId, propertyId);
      }
    },
  );

  return {
    data: data?.url,
    /**
     * Upload a photo with entity context for offline sync
     * @param params.file - The file to upload
     * @param params.entityType - Entity type (e.g., 'trunk', 'switch', 'odf')
     * @param params.entityId - Entity ID (node ID)
     * @param params.propertyId - Property ID
     */
    mutate,
    isLoading,
    isError,
    isSuccess,
    isOfflineQueued: data?.isOfflineQueued ?? false,
    uploadId: data?.uploadId,
    isOnline,
  };
};
